import { useCallback } from 'react';

interface UseDataFormattersReturn {
  formatCurrency: (value: number) => string;
  formatNumber: (value: number | string) => string;
  formatQuantity: (value: number | string) => string;
  formatCnpjCpf: (value: string) => string;
  formatMonthYear: (mes: number, ano: number) => string;
  formatDate: (dateString: string) => string;
  formatPercentage: (value: number) => string;
}

export const useDataFormatters = (): UseDataFormattersReturn => {
  const formatCurrency = useCallback((value: number): string => {

    value = Number(value);

    if (typeof value !== 'number' || isNaN(value)) {
      return 'R$ 0,00';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }, []);

  const formatNumber = useCallback((value: number | string): string => {
    const numValue = Number(value);
    if (isNaN(numValue)) return '0';
    
    return numValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }, []);

  const formatQuantity = useCallback((value: number | string): string => {
    const numValue = Number(value);
    if (isNaN(numValue)) return '0,00';
    
    return numValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }, []);

  const formatCnpjCpf = useCallback((value: string): string => {
    if (!value) return 'N/A';
    
    const digits = value.replace(/\D/g, '');
    
    if (digits.length === 11) {
      // CPF: 000.000.000-00
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (digits.length === 14) {
      // CNPJ: 00.000.000/0000-00
      return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    
    return value;
  }, []);

  const formatMonthYear = useCallback((mes: number, ano: number): string => {
    const monthNames = [
      'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ];
    
    if (mes < 1 || mes > 12) {
      return 'Mês inválido';
    }
    
    return `${monthNames[mes - 1]}/${ano}`;
  }, []);

  const formatDate = useCallback((dateString: string): string => {
    if (!dateString) return 'Data inválida';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Data inválida';
      }
      
      return date.toLocaleDateString('pt-BR');
    } catch (error) {
      return 'Data inválida';
    }
  }, []);

  const formatPercentage = useCallback((value: number): string => {
    if (typeof value !== 'number' || isNaN(value)) {
      return '0%';
    }
    
    return value.toLocaleString('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 2
    });
  }, []);

  return {
    formatCurrency,
    formatNumber,
    formatQuantity,
    formatCnpjCpf,
    formatMonthYear,
    formatDate,
    formatPercentage
  };
}; 